import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import CategorySelect from './CategorySelect';
import { StudioAnnotation, isRangeAnnotation } from './annotationModel';
import { formatDuration, formatTimecode, parseTimecode } from '../../utils/timecode';

interface AnnotationEditDialogProps {
  open: boolean;
  annotation: StudioAnnotation | null;
  fps: number;
  /** Video length in seconds; 0 when unknown. */
  duration: number;
  getPlayhead: () => number;
  onCancel: () => void;
  onSave: (next: { text: string; category: string; time: number; endTime?: number }) => void;
}

const TC_INPUT_SX = { fontVariantNumeric: 'tabular-nums', fontFamily: '"JetBrains Mono", "SFMono-Regular", Menlo, monospace' } as const;

/**
 * Edit a note's text, category and exact timing. Times are typed as timecode
 * (HH:MM:SS:FF), m:ss or seconds, or taken from the playhead.
 */
const AnnotationEditDialog: React.FC<AnnotationEditDialogProps> = ({
  open,
  annotation,
  fps,
  duration,
  getPlayhead,
  onCancel,
  onSave,
}) => {
  const [text, setText] = useState('');
  const [category, setCategory] = useState('general');
  const [startText, setStartText] = useState('');
  const [endText, setEndText] = useState('');
  const [isRange, setIsRange] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !annotation) return;
    setText(annotation.text);
    setCategory(annotation.category || 'general');
    setStartText(formatTimecode(annotation.time, fps));
    const range = isRangeAnnotation(annotation);
    setIsRange(range);
    setEndText(range ? formatTimecode(annotation.endTime as number, fps) : formatTimecode(annotation.time + 2, fps));
    setError(null);
    // Only when a different note is opened.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, annotation?.id]);

  const start = parseTimecode(startText, fps);
  const end = isRange ? parseTimecode(endText, fps) : null;
  const startInvalid = start === null || (duration > 0 && start > duration + 1e-6);
  const endInvalid = isRange && (end === null || (duration > 0 && end > duration + 1e-6) || (start !== null && end <= start));

  const handleSave = () => {
    if (!text.trim()) {
      setError('Write the note before saving.');
      return;
    }
    if (startInvalid) {
      setError(duration > 0 ? `Enter a start time between 00:00:00:00 and ${formatTimecode(duration, fps)}.` : 'Enter a valid start time.');
      return;
    }
    if (endInvalid) {
      setError('The end time must be after the start time and within the video.');
      return;
    }
    onSave({
      text: text.trim(),
      category,
      time: start as number,
      ...(isRange ? { endTime: end as number } : {}),
    });
  };

  const helper = (value: number | null, invalid: boolean) =>
    invalid ? 'HH:MM:SS:FF, m:ss or seconds' : value !== null ? `${value.toFixed(2)}s` : ' ';

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="sm" fullWidth aria-labelledby="edit-annotation-title">
      <DialogTitle id="edit-annotation-title" sx={{ fontWeight: 700 }}>Edit annotation</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <TextField
            autoFocus
            label="Note"
            multiline
            minRows={3}
            fullWidth
            value={text}
            onChange={(event) => setText(event.target.value)}
          />
          <CategorySelect value={category} onChange={setCategory} fullWidth />

          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start', flexWrap: 'wrap' }}>
            <TextField
              label={isRange ? 'In' : 'Time'}
              size="small"
              value={startText}
              onChange={(event) => setStartText(event.target.value)}
              error={startInvalid}
              helperText={helper(start, startInvalid)}
              inputProps={{ 'aria-label': isRange ? 'In timecode' : 'Timecode', style: TC_INPUT_SX }}
              sx={{ width: 170 }}
            />
            <Button size="small" onClick={() => setStartText(formatTimecode(getPlayhead(), fps))} sx={{ mt: 0.5 }}>
              Use playhead
            </Button>
            <FormControlLabel
              sx={{ ml: 'auto' }}
              control={<Switch checked={isRange} onChange={(event) => setIsRange(event.target.checked)} />}
              label="Range"
            />
          </Box>

          {isRange && (
            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start', flexWrap: 'wrap' }}>
              <TextField
                label="Out"
                size="small"
                value={endText}
                onChange={(event) => setEndText(event.target.value)}
                error={endInvalid}
                helperText={helper(end, endInvalid)}
                inputProps={{ 'aria-label': 'Out timecode', style: TC_INPUT_SX }}
                sx={{ width: 170 }}
              />
              <Button size="small" onClick={() => setEndText(formatTimecode(getPlayhead(), fps))} sx={{ mt: 0.5 }}>
                Use playhead
              </Button>
              {start !== null && end !== null && end > start && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1, ml: 'auto', fontVariantNumeric: 'tabular-nums' }}>
                  Duration {formatDuration(end - start)}
                </Typography>
              )}
            </Box>
          )}

          {error && <Alert severity="error">{error}</Alert>}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onCancel}>Cancel</Button>
        <Button variant="contained" onClick={handleSave}>Save changes</Button>
      </DialogActions>
    </Dialog>
  );
};

export default AnnotationEditDialog;
